import React, { useEffect, useState, useRef, useImperativeHandle, useMemo, forwardRef, useCallback } from 'react';

import stringify from '../utils/stableStringify';
import KhongDau from 'khong-dau';
import AntLocalization from '../utils/AntLocalization';
import AntCondition from '../utils/AntCondition';
import AntString from '../utils/AntString';
import { Template } from 'devextreme-react/core/template';
import { Validator } from 'devextreme-react/validator';
import { SelectBox } from 'devextreme-react';
import AntQueryStore from './AntQueryStore';
import CustomStore from 'devextreme/data/custom_store';

export default forwardRef(function (props, ref) {
    const [mainValue, setMainValue] = useState(props.defaultValue);
    const [searchText, setSearchText] = useState('');
    const boxRef = useRef();

    useImperativeHandle(ref, () => boxRef.current?.instance);

    const getSearchConditions = useCallback(() => {
        const obj = {
            lkTableName: props.lkTableName ?? props.options?.table ?? props.table,
            lkName: !props.url && props.lookupName ? props.lookupName : undefined
        };

        if (props.searchConditions && typeof props.searchConditions === 'object') {
            Object.entries(props.searchConditions).forEach(([k, v]) => {
                if (v !== undefined) obj[k] = v;
            });
        }

        // Không có form (ô nhập trực tiếp trên lưới): giá trị lấy từ dòng/đối tượng cha.
        const formData = props.formRef?.current?.instance?.option('formData') || (props.parentData && typeof props.parentData === 'object' ? props.parentData : null);
        if (props.parentFields && formData) {
            props.parentFields.forEach((field) => {
                if (typeof field === 'object' && field !== null) {
                    const { field: parentField, alias: aliasField } = field;
                    const val = formData[parentField];
                    if (val !== undefined && val !== null) obj[aliasField] = val;
                } else {
                    const val = formData[field];
                    if (val !== undefined && val !== null) obj[field] = val;
                }
            });
        }

        // Clean up nulls and undefineds
        Object.keys(obj).forEach((key) => {
            if (obj[key] === null || obj[key] === undefined) {
                delete obj[key];
            }
        });

        obj.isSearching = props.isSearching ?? true;
        return obj;
    }, [props.lkTableName, props.options?.table, props.table, props.url, props.lookupName, props.searchConditions, props.parentFields, props.formRef, props.parentData, props.isSearching]);

    const getQueryFields = useCallback(() => {
        const valueExpr = props.options?.valueExpr;
        const displayExpr = props.options?.displayExpr;
        const keyExpr = props.options?.keyExpr;

        const set = new Set(['created_at', 'checksum', 'id', 'code']);
        if (valueExpr) set.add(valueExpr);
        if (displayExpr) set.add(displayExpr);
        if (keyExpr) set.add(keyExpr);

        if (props.getQueryFields) {
            const fields = props.getQueryFields();
            if (Array.isArray(fields)) fields.forEach(f => f && set.add(f));
        }

        return Array.from(set);
    }, [props.options, props.getQueryFields]);

    const getOrderings = useCallback(() => {
        return Array.isArray(props.ordering) ? props.ordering : null;
    }, [props.ordering]);

    const getSearchText = useCallback(() => searchText, [searchText]);

    const renderButton = useCallback(() => (
        <i
            className="dx-icon dx-icon-search"
            style={{ marginTop: '8px', display: 'inline-block', fontSize: '20px' }}
        />
    ), []);

    const onOpened = useCallback((e) => {
        if (props.acceptCustomValue) {
            setTimeout(() => e.component.focus());
        }
    }, [props.acceptCustomValue]);

    const initClientDataSource = useCallback(() => {
        const formData = props.formRef?.current?.instance?.option('formData');
        const { valueExpr, displayExpr, dataSource: rawDataSource } = props.options;

        if (!props.isRemote || props.useCustomStore) {
            let items = Array.isArray(rawDataSource) ? rawDataSource : [];
            if (items.length > 0) {
                items = items.map((v) => {
                    if (typeof v === 'object') {
                        const rawTitle = v[displayExpr] ?? v[valueExpr];
                        return {
                            ...v,
                            [displayExpr]: AntLocalization.translateText(AntCondition.getValue(formData, rawTitle))
                        };
                    }
                    return {
                        [valueExpr]: v,
                        [displayExpr]: AntLocalization.translateText(AntCondition.getValue(formData, v))
                    };
                });
            }

            if (props.acceptCustomValue) {
                return items.map((v) => v[valueExpr]);
            }

            if (props.useCustomStore) {
                const orgItems = JSON.parse(stringify(items));
                return new CustomStore({
                    useDefaultSearch: false,
                    byKey: (keyValue) => orgItems.find((v) => v[valueExpr] === keyValue),
                    load: (loadOptions) => {
                        const searchValue = loadOptions.searchValue;
                        if (searchValue === null || searchValue === undefined) return Promise.resolve(orgItems);

                        const kdSearchValue = KhongDau(AntString.compoundUnicode(String(searchValue)).toLowerCase().trim());
                        const filtered = orgItems.filter((item) => {
                            const kdItemValue = KhongDau(AntString.compoundUnicode(String(item[displayExpr])).toLowerCase().trim());
                            if (kdItemValue.startsWith(kdSearchValue)) return true;

                            const searchWords = kdSearchValue.split(' ');
                            const itemWords = kdItemValue.split(' ');

                            let wordMatch = true;
                            const tempItemWords = [...itemWords];
                            for (const word of searchWords) {
                                const idx = tempItemWords.indexOf(word);
                                if (idx === -1) {
                                    wordMatch = false;
                                    break;
                                }
                                tempItemWords.splice(idx, 1);
                            }
                            if (wordMatch) return true;

                            const searchLetters = kdSearchValue.split('').filter(s => s.trim()).join('');
                            const itemLetters = itemWords.filter(s => s.trim()).map(s => s[0]).join('');
                            return itemLetters.startsWith(searchLetters);
                        });
                        return Promise.resolve(filtered);
                    }
                });
            }
            return items;
        }
        return undefined;
    }, [props.options, props.isRemote, props.useCustomStore, props.acceptCustomValue, props.formRef]);

    const initRemoteDataSource = useCallback(() => {
        const { valueExpr, displayExpr, keyExpr } = props.options;
        if (props.isRemote && !props.useCustomStore) {
            const store = new AntQueryStore({
                key: keyExpr ?? 'id',
                API: props.API,
                url: props.url,
                loadFunc: 'postLookup',
                isScrolling: true,
                remotePaging: props.remotePaging ?? true,
                getSearchConditions,
                getQueryFields,
                getOrderings
            });

            const formData = props.formRef?.current?.instance?.option('formData');
            const formField = props.formDisplayField ?? String(props.dataField).replace('_code', '_name');
            if (formData?.[formField] !== undefined) {
                store.addDefaultData([
                    {
                        [valueExpr]: formData[props.dataField],
                        [displayExpr]: formData[formField]
                    }
                ]);
            }
            return store;
        }
        return null;
    }, [props.options, props.isRemote, props.useCustomStore, props.API, props.url, props.remotePaging, props.formDisplayField, props.dataField, props.formRef, getSearchConditions, getQueryFields, getOrderings]);

    const clientDatasource = useMemo(() => initClientDataSource(), [initClientDataSource]);
    const remoteDataSource = useMemo(() => initRemoteDataSource(), [initRemoteDataSource]);

    useEffect(() => {
        if (remoteDataSource) {
            remoteDataSource.reInit({
                key: props.options?.keyExpr ?? remoteDataSource.options?.key ?? 'id',
                API: remoteDataSource.API,
                url: remoteDataSource.url,
                loadFunc: 'postLookup',
                isScrolling: true,
                remotePaging: remoteDataSource.remotePaging,
                getSearchConditions,
                getQueryFields,
                getOrderings,
                getSearchText
            });
        }
    }, [remoteDataSource, props.options?.keyExpr, getSearchConditions, getQueryFields, getOrderings, getSearchText]);

    const finalValueExpr = props.acceptCustomValue ? null : props.options.valueExpr;
    const finalDisplayExpr = props.acceptCustomValue ? null : props.options.displayExpr;

    const slbDatasource = props.useCustomStore ? clientDatasource : (props.isRemote ? remoteDataSource?.instance() : undefined);
    const slbItems = (!props.isRemote && !props.useCustomStore) ? clientDatasource : undefined;
    const displayValueFormatter = useCallback((displayValue) => {
        if (props.options?.displayValueFormatter) return props.options.displayValueFormatter(displayValue);
        if (!(clientDatasource instanceof Array) || !finalValueExpr || !finalDisplayExpr) return displayValue;

        const selectedItem = clientDatasource.find((item) => item && item[finalValueExpr] === mainValue);
        return selectedItem ? selectedItem[finalDisplayExpr] : displayValue;
    }, [props.options, clientDatasource, finalValueExpr, finalDisplayExpr, mainValue]);

    return (
        <SelectBox
            ref={boxRef}
            acceptCustomValue={Boolean(props.acceptCustomValue)}
            searchEnabled={props.searchEnabled ?? true}
            searchMode="contains"
            searchTimeout={200}
            showDataBeforeSearch={true}
            placeholder={props.placeholder ?? '...'}
            showClearButton={props.showClearButton ?? true}
            showDropDownButton={props.showDropDownButton ?? false}
            {...props.options}
            name={props.name}
            focusStateEnabled={props.focusStateEnabled ?? true}
            noDataText={AntLocalization.translateText({ vi: 'Không có dữ liệu !', en: 'Have no data !', fr: 'Aucune donnée !', es: '¡Sin datos!', id: 'Tidak ada data!', ja: 'データがありません！', zh: '没有数据！', ko: '데이터 없음!', hi: 'कोई डेटा नहीं!', ar: 'لا توجد بيانات!' })}
            value={mainValue}
            dataSource={slbDatasource}
            items={slbItems}
            valueExpr={finalValueExpr}
            displayExpr={finalDisplayExpr}
            displayValueFormatter={displayValueFormatter}
            dropDownOptions={{
                width: '100%',
                height: props.height ?? 250,
                wrapperAttr: { class: 'ant-dropdown-topmost', style: 'z-index: 2147483647 !important' }
            }}
            dropDownButtonTemplate={props.dataField === 'code' || props.dataField === 'id' ? 'renderButton' : 'dropDownButton'}
            onOpened={onOpened}
            onInput={(e) => {
                if (!e.component.option('opened')) e.component.open();
                setSearchText(e.component.option('text'));
            }}
            onKeyDown={(e) => {
                if (e.event.key === 'Enter' || e.event.key === 'Tab') e.component.close();
            }}
            onFocusIn={(e) => {
                const inputEl = e.element.querySelector('.dx-texteditor-input');
                if (inputEl) {
                    inputEl.selectionStart = 0;
                    inputEl.selectionEnd = inputEl.value.length;
                }
                props.onFocusIn?.({ dataField: props.dataField });
            }}
            onFocusOut={(e) => {
                const newValue = e.component.option('value');
                if (newValue !== mainValue) setMainValue(newValue);
            }}
            onValueChanged={(e) => {
                setMainValue(e.value);
                props.onValueChanged?.({
                    value: e.value,
                    rowData: e.component.option('selectedItem'),
                    previousValue: e.previousValue
                });
            }}
        >
            {props.validationRules && <Validator validationRules={props.validationRules} dataField={props.dataField} />}
            <Template name="renderButton" render={renderButton} />
        </SelectBox>
    );
});
